(function () {
  /**
   * Multi-step survey.
   *
   * All steps live in a single <form> and are toggled with the [hidden]
   * attribute, so every field — including those on steps the visitor has
   * scrolled past — is still submitted in one POST.
   *
   * The form carries `novalidate` on purpose: native constraint validation
   * refuses to submit when an invalid field is hidden, and cannot focus it to
   * explain why. Validation is therefore driven from here, per step.
   */
  if (!customElements.get('b4u-survey')) {
    class B4USurvey extends HTMLElement {
      connectedCallback() {
        this.form     = this.querySelector('.b4u-sfs__form');
        this.steps    = Array.from(this.querySelectorAll('.b4u-sfs__step'));
        this.markers  = Array.from(this.querySelectorAll('.b4u-sfs__step-marker'));
        this.media    = Array.from(this.querySelectorAll('.b4u-sfs__media-item'));
        this.stepper  = this.querySelector('.b4u-sfs__stepper');
        this.counter  = this.querySelector('[data-sfs-counter]');
        this.alert    = this.querySelector('[data-sfs-alert]');
        this.backBtn  = this.querySelector('[data-sfs-back]');
        this.nextBtn  = this.querySelector('[data-sfs-next]');
        this.submitBtn = this.querySelector('[data-sfs-submit]');

        if (!this.steps.length) return;

        this.validate  = this.dataset.validate === 'true';
        this.scrollTo  = this.dataset.scroll === 'true';
        this.storeKey  = this.dataset.saveKey || '';
        this.designMode = typeof Shopify !== 'undefined' && Shopify.designMode;

        this.uploadUrl   = this.dataset.uploadEndpoint || '';
        this.fileInput   = this.querySelector('[data-sfs-upload]');
        this.fileLinkEl  = this.querySelector('[data-sfs-upload-url]');
        this.fileStatus  = this.querySelector('[data-sfs-upload-status]');

        this.index = 0;

        this._bind();
        this._bindGuides();
        this._bindVideos();
        if (this.storeKey && !this.designMode) this._restore();
        this._render();
      }

      disconnectedCallback() {
        if (this._onBlockSelect) {
          document.removeEventListener('shopify:block:select', this._onBlockSelect);
        }
        if (this._mq && this._onViewport) {
          this._mq.removeEventListener('change', this._onViewport);
        }
      }

      /**
       * The instruction guide is a <details>. The desktop design shows it
       * expanded with no toggle; on mobile it collapses so the form stays
       * reachable. CSS cannot set the open attribute, so drive it here.
       */
      _bindGuides() {
        const guides = Array.from(this.querySelectorAll('[data-sfs-guide]'));
        if (!guides.length) return;

        this._mq = window.matchMedia('(max-width: 767px)');
        this._onViewport = () => {
          guides.forEach((guide) => { guide.open = !this._mq.matches; });
        };
        this._onViewport();
        this._mq.addEventListener('change', this._onViewport);

        // Keep the summary inert on desktop, where it is not a control
        guides.forEach((guide) => {
          const head = guide.querySelector('.b4u-sfs__guide-head');
          if (!head) return;
          head.addEventListener('click', (e) => {
            if (!this._mq.matches) e.preventDefault();
          });
        });
      }

      /**
       * The guide's video box shows a thumbnail and play button by default.
       * A click inserts an inline iframe on top of them rather than
       * navigating away — YouTube chrome (controls, related videos,
       * keyboard shortcuts) is switched off via the embed params.
       */
      _bindVideos() {
        this.querySelectorAll('[data-sfs-video]').forEach((trigger) => {
          trigger.addEventListener('click', () => {
            const box = trigger.closest('.b4u-sfs__guide-media--video');
            const id  = trigger.dataset.sfsVideoId;
            if (!box || !id || box.classList.contains('is-playing')) return;

            const iframe = document.createElement('iframe');
            iframe.src = 'https://www.youtube.com/embed/' + id +
              '?autoplay=1&controls=0&rel=0&modestbranding=1&iv_load_policy=3&disablekb=1&playsinline=1';
            iframe.allow = 'autoplay; encrypted-media';
            iframe.title = trigger.getAttribute('aria-label') || 'Video';

            box.appendChild(iframe);
            box.classList.add('is-playing');
          });
        });
      }

      _bind() {
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => {
            if (this.validate && !this._validateStep(this.index)) return;
            this._goTo(this.index + 1);
          });
        }

        if (this.backBtn) {
          this.backBtn.addEventListener('click', () => this._goTo(this.index - 1));
        }

        // Completed steps are clickable shortcuts back
        this.markers.forEach((marker, i) => {
          marker.addEventListener('click', () => {
            if (i < this.index) this._goTo(i);
          });
        });

        // Clear a field's error as soon as the visitor corrects it
        this.querySelectorAll('.b4u-sfs__input').forEach((input) => {
          input.addEventListener('input', () => {
            if (input.getAttribute('aria-invalid') === 'true') this._clearError(input);
            if (this.storeKey && !this.designMode) this._persist();
          });
        });

        this.querySelectorAll('.b4u-sfs__segment-input').forEach((radio) => {
          radio.addEventListener('change', () => {
            const group = radio.closest('.b4u-sfs__segmented');
            if (group && group.getAttribute('aria-invalid') === 'true') this._clearError(group);
            if (this.storeKey && !this.designMode) this._persist();
          });
        });

        // The native file control is invisible, so echo the chosen name back
        this.querySelectorAll('[data-sfs-upload]').forEach((input) => {
          input.addEventListener('change', () => {
            const hint = input.parentElement.querySelector('[data-sfs-upload-hint]');
            if (hint) {
              const file = input.files && input.files[0];
              hint.textContent = file ? file.name : (hint.dataset.default || '');
            }
            // A different file deserves a fresh attempt, and any link from a
            // previous one is now stale
            this._skipUpload = false;
            if (this.fileLinkEl) this.fileLinkEl.value = '';
            this._setUploadStatus('');
            this._hideAlert();
          });
        });

        if (this.form) {
          this.form.addEventListener('submit', (e) => this._onSubmit(e));
        }

        if (this.designMode) {
          // Jump to whichever step the merchant selects in the editor
          this._onBlockSelect = (e) => {
            const step = this.steps.findIndex((s) => s.dataset.blockId === e.detail.blockId);
            if (step > -1) this._goTo(step, { skipValidation: true, skipScroll: true });
          };
          document.addEventListener('shopify:block:select', this._onBlockSelect);
        }
      }

      /* ── Navigation ───────────────────────────────────────── */

      _goTo(index, opts) {
        const options = opts || {};
        if (index < 0 || index >= this.steps.length) return;

        this.index = index;
        this._render();

        if (this.scrollTo && !options.skipScroll) {
          const top = this.getBoundingClientRect().top + window.pageYOffset - 100;
          window.scrollTo({ top: top, behavior: 'smooth' });
        }

        // Move focus to the new step's heading so screen readers announce it
        const heading = this.steps[index].querySelector('.b4u-sfs__step-heading');
        if (heading) {
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
        }

        if (this.storeKey && !this.designMode) this._persist();
      }

      _render() {
        const last = this.steps.length - 1;

        this.steps.forEach((step, i) => {
          step.hidden = i !== this.index;
        });

        // Each step can carry its own media panel
        this.media.forEach((item, i) => {
          item.hidden = i !== this.index;
        });

        this.markers.forEach((marker, i) => {
          // Mutually exclusive by construction: i < index vs i === index
          marker.classList.toggle('b4u-sfs__step-marker--done', i < this.index);
          marker.classList.toggle('b4u-sfs__step-marker--current', i === this.index);
          if (i === this.index) {
            marker.setAttribute('aria-current', 'step');
          } else {
            marker.removeAttribute('aria-current');
          }
        });

        if (this.stepper) {
          const progress = last > 0 ? this.index / last : 0;
          this.stepper.style.setProperty('--sfs-progress', String(progress));
        }

        if (this.counter) {
          const template = this.counter.dataset.template || 'Step [current] of [total]';
          this.counter.textContent = template
            .replace('[current]', String(this.index + 1))
            .replace('[total]', String(this.steps.length));
        }

        if (this.backBtn)   this.backBtn.hidden   = this.index === 0;
        if (this.nextBtn)   this.nextBtn.hidden   = this.index === last;
        if (this.submitBtn) this.submitBtn.hidden = this.index !== last;
      }

      /* ── Validation ───────────────────────────────────────── */

      /**
       * Marks every field on a step and returns one entry per problem:
       * { focus, label, step }. Empty array means the step is complete.
       */
      _collectStep(index) {
        const step = this.steps[index];
        const problems = [];

        step.querySelectorAll('.b4u-sfs__input').forEach((input) => {
          const message = this._checkField(input);
          if (message) {
            this._showError(input, message);
            problems.push({ focus: input, label: this._labelFor(input), step: index });
          } else {
            this._clearError(input);
          }
        });

        // Segmented choices are radio groups, so "filled in" means one is checked
        step.querySelectorAll('.b4u-sfs__segmented[data-sfs-required="true"]').forEach((group) => {
          if (group.querySelector('input[type="radio"]:checked')) {
            this._clearError(group);
          } else {
            this._showError(group, group.dataset.errorRequired || 'Please choose an option.');
            problems.push({
              focus: group.querySelector('input[type="radio"]'),
              label: this._labelFor(group),
              step: index
            });
          }
        });

        return problems;
      }

      _validateStep(index) {
        const problems = this._collectStep(index);
        if (problems.length) {
          this._showAlert(problems);
          return false;
        }
        this._hideAlert();
        return true;
      }

      /** Visible field name, for the summary list. */
      _labelFor(el) {
        let source = null;
        if (el.id) source = this.querySelector('label[for="' + el.id + '"]');
        if (!source) {
          const ref = el.getAttribute('aria-labelledby');
          if (ref) source = this.querySelector('#' + ref);
        }
        if (!source) return el.name || 'This field';
        const name = source.textContent.trim().replace(/\*$/, '').trim();

        // Only qualify a label that repeats within the step (Length/Width appear
        // once per foot); a unique label must not inherit a preceding group.
        const step = el.closest('.b4u-sfs__step');
        if (!step) return name;

        const sameName = Array.from(step.querySelectorAll('.b4u-sfs__label'))
          .filter((l) => l.textContent.trim().replace(/\*$/, '').trim() === name).length;
        if (sameName < 2) return name;

        const field = el.closest('.b4u-sfs__field');
        let node = field ? field.previousElementSibling : null;
        while (node) {
          if (node.classList.contains('b4u-sfs__group-label')) {
            return node.textContent.trim() + ' — ' + name;
          }
          node = node.previousElementSibling;
        }
        return name;
      }

      /* ── Error summary ────────────────────────────────────── */

      _showAlert(problems, heading) {
        if (!this.alert) {
          if (problems[0] && problems[0].focus) problems[0].focus.focus();
          return;
        }

        const titleEl = this.alert.querySelector('[data-sfs-alert-title]');
        const listEl  = this.alert.querySelector('[data-sfs-alert-list]');

        if (titleEl) {
          titleEl.textContent = heading || (problems.length === 1
            ? (this.alert.dataset.titleSingle || 'Please complete one more field before continuing.')
            : (this.alert.dataset.title || 'Please complete [count] fields before continuing.')
                .replace('[count]', String(problems.length)));
        }

        if (listEl) {
          listEl.textContent = '';
          problems.forEach((problem) => {
            const item = document.createElement('li');
            item.className = 'b4u-sfs__alert-item';

            // Not every entry points at a field to fix — an upload failure is
            // just an explanation, with nothing to jump to
            if (problem.plain) {
              item.textContent = problem.label;
              listEl.appendChild(item);
              return;
            }

            const link = document.createElement('button');
            link.type = 'button';
            link.className = 'b4u-sfs__alert-link';
            link.textContent = problem.label;
            link.addEventListener('click', () => {
              if (problem.step !== this.index) this._goTo(problem.step, { skipScroll: true });
              if (problem.focus) problem.focus.focus();
            });

            item.appendChild(link);
            listEl.appendChild(item);
          });
        }

        this.alert.hidden = false;
        // Focus the summary rather than the field, so the whole list is announced
        this.alert.focus();
      }

      _hideAlert() {
        if (this.alert) this.alert.hidden = true;
      }

      /**
       * Returns an error message, or '' when the field is acceptable.
       * Empty optional fields are always acceptable.
       */
      _checkField(input) {
        const value    = (input.value || '').trim();
        const required = input.dataset.sfsRequired === 'true';

        if (required && !value) {
          return input.dataset.errorRequired || 'This field is required.';
        }
        if (value && !input.checkValidity()) {
          return input.dataset.errorInvalid || input.validationMessage;
        }
        return '';
      }

      _showError(input, message) {
        input.setAttribute('aria-invalid', 'true');
        const target = this._errorEl(input);
        if (target) {
          target.textContent = message;
          target.classList.add('b4u-sfs__error--visible');
        }
      }

      _clearError(input) {
        input.removeAttribute('aria-invalid');
        const target = this._errorEl(input);
        if (target) {
          target.textContent = '';
          target.classList.remove('b4u-sfs__error--visible');
        }
      }

      _errorEl(input) {
        const id = input.getAttribute('aria-describedby');
        return id ? this.querySelector('#' + id) : null;
      }

      _onSubmit(e) {
        if (this.validate) {
          // Re-check every step, not just the visible one: saved progress or a
          // stepper jump can leave an earlier step incomplete. Collect across all
          // steps so the summary names everything that still needs attention.
          let problems = [];
          let firstBadStep = -1;
          for (let i = 0; i < this.steps.length; i++) {
            const stepProblems = this._collectStep(i);
            if (stepProblems.length && firstBadStep === -1) firstBadStep = i;
            problems = problems.concat(stepProblems);
          }

          if (problems.length) {
            e.preventDefault();
            this._goTo(firstBadStep, { skipScroll: true });
            this._showAlert(problems);
            return;
          }

          this._hideAlert();
        }

        // The POST cannot carry the file, so store it first and let the form
        // follow with a link once that succeeds
        if (this._pendingUpload()) {
          e.preventDefault();
          this._uploadThenSubmit();
          return;
        }

        if (this.storeKey) this._clearStore();
      }

      /* ── Image upload ─────────────────────────────────────── */

      _pendingUpload() {
        if (!this.uploadUrl || this._skipUpload) return false;
        if (!this.fileInput || !this.fileInput.files || !this.fileInput.files.length) return false;
        // Already uploaded — a second press should just submit
        return !(this.fileLinkEl && this.fileLinkEl.value);
      }

      /**
       * Stores the image, writes the returned link into the form, then submits
       * natively. A failure never costs the visitor their answers: it explains
       * itself and arms the next press to go through without the image.
       */
      async _uploadThenSubmit() {
        const file  = this.fileInput.files[0];
        const maxMb = parseFloat(this.dataset.uploadMaxMb || '10');

        this._setBusy(true);

        try {
          const blob = await this._prepareFile(file);

          if (blob.size > maxMb * 1024 * 1024) {
            this._setBusy(false);
            this._uploadFailed(
              (this.dataset.uploadErrorSize || 'That image is larger than [max]MB.')
                .replace('[max]', String(maxMb))
            );
            return;
          }

          const payload = {
            token:    this.dataset.uploadToken || '',
            filename: file.name,
            mimeType: blob.type || file.type,
            label:    this._uploadLabel(),
            data:     await this._toBase64(blob)
          };

          let url;
          try {
            url = await this._send(payload);
          } catch (blip) {
            url = await this._send(payload); // one retry, for a dropped connection
          }

          if (this.fileLinkEl) this.fileLinkEl.value = url;
          this._setUploadStatus('');
          this._setBusy(false);
          if (this.storeKey) this._clearStore();

          this._submitForm();
        } catch (err) {
          this._setBusy(false);
          this._uploadFailed(this.dataset.uploadError || 'Press Send again to submit without it.');
        }
      }

      /**
       * requestSubmit rather than submit: Shopify's storefront captcha script
       * hooks the submit event to attach its token, and a bare form.submit()
       * fires no event, so the POST would arrive without one.
       *
       * Re-entering this handler is harmless — the link is set by now, so
       * _pendingUpload is false and the submit falls straight through.
       */
      _submitForm() {
        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          this.form.submit();
        }
      }

      async _send(payload) {
        // text/plain keeps this a "simple" request. Apps Script does not answer
        // a CORS preflight, so a JSON content type or a custom header would fail
        // before the upload was ever attempted.
        const response = await fetch(this.uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error('http_' + response.status);

        const data = await response.json();
        if (!data || !data.ok || !data.url) throw new Error((data && data.error) || 'rejected');
        return data.url;
      }

      /**
       * Shrinks the image in the browser. A phone photo runs to several MB and
       * base64 adds a third on top, while nothing beyond a couple of thousand
       * pixels helps judge fit. Re-encoding also drops EXIF, so location data in
       * the original never leaves the device.
       */
      async _prepareFile(file) {
        if (!/^image\//.test(file.type)) return file;

        const source = await this._decode(file);
        if (!source) return file;

        const width  = source.naturalWidth  || source.width;
        const height = source.naturalHeight || source.height;
        if (!width || !height) return file;

        const maxPx = parseInt(this.dataset.uploadMaxPx || '2000', 10);
        const scale = Math.min(1, maxPx / Math.max(width, height));
        const w = Math.round(width * scale);
        const h = Math.round(height * scale);

        const canvas = document.createElement('canvas');
        canvas.width  = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(source, 0, 0, w, h);
        if (source.close) source.close();

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
        return blob || file;
      }

      _decode(file) {
        // from-image so a portrait photo is not written out sideways: canvas
        // ignores EXIF orientation unless the bitmap already has it applied
        if (window.createImageBitmap) {
          return createImageBitmap(file, { imageOrientation: 'from-image' })
            .catch(() => this._decodeViaImg(file));
        }
        return this._decodeViaImg(file);
      }

      _decodeViaImg(file) {
        return new Promise((resolve) => {
          const url = URL.createObjectURL(file);
          const img = new Image();
          img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
          img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
          img.src = url;
        });
      }

      _toBase64(blob) {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = String(reader.result || '');
            const comma  = result.indexOf(',');
            resolve(comma > -1 ? result.slice(comma + 1) : '');
          };
          reader.onerror = () => reject(new Error('read_failed'));
          reader.readAsDataURL(blob);
        });
      }

      // Names the stored file after the visitor, so the folder stays browsable
      _uploadLabel() {
        const value = (field) => {
          const el = this.form.querySelector('[name="contact[' + field + ']"]');
          return el ? (el.value || '').trim() : '';
        };
        return [value('First name'), value('Last name')].filter(Boolean).join(' ');
      }

      _uploadFailed(detail) {
        this._skipUpload = true;
        this._setUploadStatus(detail, true);

        // Says so in the notification email, rather than leaving the field blank
        // as though no image had been offered at all
        if (this.fileLinkEl) {
          this.fileLinkEl.value = 'Upload failed — please ask the customer for the photo';
        }

        const items = [{ label: detail, plain: true }];
        const nudge = this.dataset.uploadError;
        if (nudge && nudge !== detail) items.push({ label: nudge, plain: true });

        this._showAlert(items, this.dataset.uploadErrorTitle || 'Your image could not be uploaded.');
      }

      _setBusy(on) {
        if (this.submitBtn) {
          this.submitBtn.disabled = on;
          this.submitBtn.setAttribute('aria-busy', on ? 'true' : 'false');
        }
        this.classList.toggle('b4u-sfs__card--uploading', on);
        if (on) this._setUploadStatus(this.dataset.uploadBusy || 'Uploading your image…');
      }

      _setUploadStatus(text, isError) {
        if (!this.fileStatus) return;
        this.fileStatus.textContent = text || '';
        this.fileStatus.classList.toggle('b4u-sfs__upload-status--visible', !!text);
        this.fileStatus.classList.toggle('b4u-sfs__upload-status--error', !!isError);
      }

      /* ── Optional progress persistence ────────────────────── */

      _persist() {
        clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(() => {
          const data = { step: this.index, fields: {} };
          this.querySelectorAll('.b4u-sfs__input').forEach((input) => {
            if (input.name) data.fields[input.name] = input.value;
          });
          this.querySelectorAll('.b4u-sfs__segment-input:checked').forEach((radio) => {
            if (radio.name) data.fields[radio.name] = radio.value;
          });
          try {
            localStorage.setItem(this.storeKey, JSON.stringify(data));
          } catch (err) {
            // Private browsing or a full quota — progress saving is optional
          }
        }, 300);
      }

      _restore() {
        let data;
        try {
          data = JSON.parse(localStorage.getItem(this.storeKey) || 'null');
        } catch (err) {
          return;
        }
        if (!data) return;

        if (data.fields) {
          this.querySelectorAll('.b4u-sfs__input').forEach((input) => {
            if (input.name && typeof data.fields[input.name] === 'string') {
              input.value = data.fields[input.name];
            }
          });
          this.querySelectorAll('.b4u-sfs__segment-input').forEach((radio) => {
            if (radio.name && data.fields[radio.name] === radio.value) radio.checked = true;
          });
        }
        if (typeof data.step === 'number' && data.step >= 0 && data.step < this.steps.length) {
          this.index = data.step;
        }
      }

      _clearStore() {
        try {
          localStorage.removeItem(this.storeKey);
        } catch (err) {
          // Nothing to do — the submit still goes through
        }
      }
    }

    customElements.define('b4u-survey', B4USurvey);
  }
})();
